import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation } from '@tanstack/react-query'
import { Form, Formik, FormikHelpers } from 'formik'
import React from 'react'
import { LuCircleAlert, LuInfo } from 'react-icons/lu'
import { Link, useSearchParams } from 'react-router'
import invariant from 'tiny-invariant'

import { OPTIONS } from '@/components/flows/CompassFlow/options'
import RadioOptionsInput from '@/components/form/RadioOptionsInput'
import ActionButton from '@/components/misc/ActionButton'
import AddAgreementSection from '@/components/misc/CommercialAgreementsDetails/components/AddAgreementSection'
import AgreementFieldRow from '@/components/misc/CommercialAgreementsDetails/components/AgreementFieldRow'
import AgreementStatusMessage from '@/components/misc/CommercialAgreementsDetails/components/AgreementStatusMessage'
import AmendTemplatedAgreementButton from '@/components/misc/CommercialAgreementsDetails/components/AmendTemplatedAgreementButton'
import ExistingAgreements from '@/components/misc/CommercialAgreementsDetails/components/ExistingAgreements'
import RqCompliantFeeSharingTooltip from '@/components/misc/CommercialAgreementsDetails/components/RqCompliantFeeSharingTooltip'
import {
  COMMERCIAL_AGREEMENT_TAB_KEY,
  COMMERCIAL_AGREEMENT_TAB_PARAMS,
  REVIEW_COMMERCIAL_AGREEMENT_FORM_FIELDS,
  ReviewCommercialAgreementFormValues,
} from '@/components/misc/CommercialAgreementsDetails/constants'
import { COMMERCIAL_AGREEMENT_TYPE_OPTION } from '@/components/misc/CommercialAgreementsForm/constants'
import ReviewESignatureLetterBox from '@/components/misc/ReviewESignatureLetterBox'
import ServiceFeesTable from '@/components/misc/ServiceFeesTable/ServiceFeesTable'
import PreviewESignatureLetterModal from '@/components/modals/PreviewESignatureLetterModal/PreviewESignatureLetterModal'
import {
  PREVIEW_ESIGNATURE_LETTER_MODAL_PARAMS,
  usePreviewESignatureLetterModal,
} from '@/components/modals/PreviewESignatureLetterModal/usePreviewESignatureLetterModal'
import { useReviewESignatureLetterModal } from '@/components/modals/ReviewESignatureLetterModal/useReviewESignatureLetterModal'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import useCurrentCompanyOrFail from '@/hooks/useCurrentCompanyOrFail'
import { useInvalidatePanelQueries } from '@/hooks/useInvalidatePanelQueries'
import useNotyf from '@/hooks/useNotyf'
import {
  API_VIOLATION_CODE,
  COMMERCIAL_AGREEMENT_REFERRAL_DIRECTION,
  COMMERCIAL_AGREEMENT_REVIEW_STATUS,
  DOM_IDS,
  LONG_MESSAGE_DURATION,
  PENDING_PANEL_ACTION,
  REFERRAL_CONSENT_LETTER_VARIANT,
  REFERRAL_CONSENT_STAGE,
} from '@/lib/constants'
import { faArrowRight, faPencil, faTimes } from '@/lib/fontAwesomePro/regular'
import { faCheck } from '@/lib/fontAwesomePro/solid'
import { bool2Human, scrollToElement } from '@/lib/helpers/helperFunctions'
import { currentUrl } from '@/lib/helpers/routeHelpers'
import { ROUTES } from '@/lib/routes'
import commercialAgreementService from '@/lib/services/commercialAgreementService'
import constraintViolationService from '@/lib/services/constraintViolationService'
import panelService from '@/lib/services/panelService'
import { cn } from '@/lib/utils'
import { CommercialAgreementReferralDirection } from '@/types/misc'
import { ConfirmCommercialAgreementRequest } from '@/types/requests/commercial-agreements'
import {
  PanelItem,
  PanelItemCommercialAgreement,
} from '@/types/responses/panels'

interface Props {
  agreement?: PanelItemCommercialAgreement | null
  panel: PanelItem
  referralDirection: CommercialAgreementReferralDirection
  otherAgreementNeedsReview: boolean
  onConfirmAgreement: () => Promise<void>
}

interface ConfirmAgreementMutationVariables {
  values: ReviewCommercialAgreementFormValues
  formikHelpers: FormikHelpers<ReviewCommercialAgreementFormValues>
}

const DirectionAgreementDetails: React.FC<Props> = ({
  agreement,
  panel,
  referralDirection,
  otherAgreementNeedsReview,
  onConfirmAgreement,
}) => {
  const previewESignatureLetterModal = usePreviewESignatureLetterModal()
  const reviewESignatureLetterModal = useReviewESignatureLetterModal()
  const needsReview = !!agreement?.needsReview
  const notyf = useNotyf()
  const invalidatePanelsQuery = useInvalidatePanelQueries()
  const currentCompany = useCurrentCompanyOrFail()
  const otherCompany =
    currentCompany.id === panel.fromCompany.id
      ? panel.toCompany
      : panel.fromCompany
  const [searchParams, setSearchParams] = useSearchParams()

  const isOutgoingAgreement =
    commercialAgreementService.isOutgoingAgreementDirection(referralDirection)

  const isPanelAwaitingApproval = panel.pendingActions.includes(
    PENDING_PANEL_ACTION.respondToInvite,
  )

  const deletePendingAgreementMutation = useMutation({
    mutationFn: (agreement: PanelItemCommercialAgreement) =>
      commercialAgreementService.deletePendingAgreement(agreement.id),
    onSuccess: async (_response, agreement) => {
      await handleConfirmOrRejectAgreementSuccess()

      notyf.success(
        agreement.needsReview
          ? 'Changes successfully rejected'
          : 'Changes successfully cancelled',
      )
    },

    onError: (_error, agreement) => {
      notyf.error(
        agreement.needsReview
          ? 'Failed to reject changes'
          : 'Failed to cancel changes',
      )
    },
  })

  const isOnlyAgreementThatNeedsReview =
    needsReview && !otherAgreementNeedsReview

  const canConfirmAndAcceptInvite =
    isPanelAwaitingApproval &&
    agreement?.isCurrentAgreement &&
    isOnlyAgreementThatNeedsReview

  async function confirmAgreement(values: ReviewCommercialAgreementFormValues) {
    const { existingAgreementFile, isRqHandleFeeSharing } = values
    invariant(agreement, 'Can only confirm an agreement if an agreement exists')

    const request: ConfirmCommercialAgreementRequest = {
      consentLetterApprovedAt: values.clientConsentLetterApprovedAt,
      consentLetterAmendRequest: values.clientConsentLetterAmendRequest,
    }

    if (existingAgreementFile) {
      request.existingAgreementFileName = existingAgreementFile.name
      request.existingAgreementFileBase64 = existingAgreementFile.base64
    }

    if (isRqHandleFeeSharing !== null) {
      request.isRqHandleFeeSharing = isRqHandleFeeSharing
    }

    await commercialAgreementService.confirmAgreement(agreement.id, request)

    await onConfirmAgreement()
  }

  const pendingAgreement = isOutgoingAgreement
    ? panel.pendingOutgoingAgreement
    : panel.pendingIncomingAgreement

  function shouldShowAmendButton(
    agreement?: PanelItemCommercialAgreement | null,
  ): agreement is PanelItemCommercialAgreement {
    // If there's no agreement, don't show the amend button
    if (!agreement) {
      return false
    }

    // Don't allow editing agreements that were created via a template
    if (agreement.commercialAgreementTemplate) {
      return false
    }

    // If there is a pending agreement for this direction and we're viewing the
    // current agreement, don't show the amend button as the user should open
    // the pending agreement and amend that instead
    if (agreement.isCurrentAgreement) {
      return !pendingAgreement
    }

    // Must be viewing the pending agreement in which case it's ok to show
    return true
  }

  const shouldShowPendingAgreementMessage =
    agreement?.isCurrentAgreement && !!pendingAgreement

  async function invalidateQueries() {
    await invalidatePanelsQuery()
  }

  function resetCommercialAgreementTab() {
    const tabName = isOutgoingAgreement
      ? COMMERCIAL_AGREEMENT_TAB_PARAMS.outgoingAgreementTab
      : COMMERCIAL_AGREEMENT_TAB_PARAMS.incomingAgreementTab

    searchParams.set(tabName, COMMERCIAL_AGREEMENT_TAB_KEY.current)
    setSearchParams(searchParams)
  }

  async function handleConfirmOrRejectAgreementSuccess() {
    await invalidateQueries()
    resetCommercialAgreementTab()
  }

  const confirmAgreementMutation = useMutation({
    mutationFn: (variables: ConfirmAgreementMutationVariables) =>
      confirmAgreement(variables.values),
    onSuccess: async () => {
      await handleConfirmOrRejectAgreementSuccess()
      notyf.success('Agreement successfully confirmed')
    },
    onError: handleConfirmAgreementError,
  })

  const confirmAgreementAndAcceptInviteMutation = useMutation({
    mutationFn: async (variables: ConfirmAgreementMutationVariables) => {
      await confirmAgreement(variables.values)
      return panelService.acceptPanelInvite(panel.id)
    },
    onSuccess: async () => {
      await handleConfirmOrRejectAgreementSuccess()
      notyf.success(
        `${panel.fromCompany.presentationName} was added to your approved referral partners`,
      )
    },
    onError: handleConfirmAgreementError,
  })

  function handleConfirmAgreementError(error: unknown) {
    const consentLetterError = constraintViolationService.getViolationByCode(
      error,
      API_VIOLATION_CODE.unapprovedOrUnamendedConsentLetter,
    )

    if (consentLetterError) {
      notyf.errorWithoutReporting({
        message: consentLetterError.message,
        duration: LONG_MESSAGE_DURATION,
      })

      const consentLetterElement = document.getElementById(
        DOM_IDS.reviewConsentLetterBox,
      )

      if (consentLetterElement) {
        consentLetterElement.classList.remove('pulse-primary')

        setTimeout(() => {
          consentLetterElement.classList.add('pulse-primary')
        }, 10)

        scrollToElement(consentLetterElement)
      }

      return
    }

    // The existing agreement file can be uploaded while confirming, so the API
    // may reject it here, e.g. because it's a password-protected PDF
    const fileErrorMessage =
      constraintViolationService.getFileViolationMessage(error)

    if (fileErrorMessage) {
      notyf.errorWithoutReporting({
        message: fileErrorMessage,
        duration: LONG_MESSAGE_DURATION,
      })

      return
    }

    notyf.error('Failed to confirm agreement')
  }

  const renderActionButtonText = () => {
    if (agreement?.isCurrentAgreement) {
      return canConfirmAndAcceptInvite
        ? 'Confirm & accept invitation'
        : 'Confirm'
    }

    return canConfirmAndAcceptInvite
      ? 'Approve changes & accept invitation'
      : 'Approve changes'
  }

  function handleSubmit(
    values: ReviewCommercialAgreementFormValues,
    helpers: FormikHelpers<ReviewCommercialAgreementFormValues>,
  ) {
    if (canConfirmAndAcceptInvite) {
      confirmAgreementAndAcceptInviteMutation.mutate({
        values,
        formikHelpers: helpers,
      })

      return
    }

    if (needsReview) {
      confirmAgreementMutation.mutate({ values, formikHelpers: helpers })
      return
    }

    throw new Error('Did not expect to reach this point')
  }

  function renderActionButton() {
    const buttonClassName =
      'flex items-center gap-2 w-full sm:w-auto w-full sm:w-auto justify-center'

    if (canConfirmAndAcceptInvite) {
      return (
        <ActionButton
          type="submit"
          variant="success"
          size="lg"
          isProcessing={confirmAgreementAndAcceptInviteMutation.isPending}
          style={{ minWidth: '110px' }}
          className={buttonClassName}
        >
          <FontAwesomeIcon icon={faCheck} className="text-md" />
          {renderActionButtonText()}
        </ActionButton>
      )
    }

    if (needsReview) {
      return (
        <ActionButton
          type="submit"
          variant="success"
          size="lg"
          isProcessing={confirmAgreementMutation.isPending}
          style={{ minWidth: '110px' }}
          className={buttonClassName}
        >
          <FontAwesomeIcon icon={faCheck} className="text-md" />
          {renderActionButtonText()}
        </ActionButton>
      )
    }

    return null
  }

  const initialValues: ReviewCommercialAgreementFormValues = {
    existingAgreementFile: null,
  }

  const isPendingAgreementAwaitingReviewByUs =
    pendingAgreement?.reviewStatus ===
    COMMERCIAL_AGREEMENT_REVIEW_STATUS.AWAITING_REVIEW_BY_US

  const isIncomingAgreement =
    referralDirection === COMMERCIAL_AGREEMENT_REFERRAL_DIRECTION.INCOMING

  function shouldReviewESignatureLetter(
    agreement?: PanelItemCommercialAgreement | null,
  ): agreement is PanelItemCommercialAgreement {
    // Only show review box for incoming agreements
    if (!isIncomingAgreement || !agreement) {
      return false
    }

    // Don't require review is agreement is already complete
    if (agreement.isAgreementComplete) {
      return false
    }

    return agreement.isCurrentAgreement
      ? panel.pendingActions.includes(
          PENDING_PANEL_ACTION.reviewCurrentIncomingAgreementConsentLetter,
        )
      : panel.pendingActions.includes(
          PENDING_PANEL_ACTION.reviewPendingIncomingAgreementConsentLetter,
        )
  }

  const agreementTab = isOutgoingAgreement
    ? COMMERCIAL_AGREEMENT_TAB_PARAMS.outgoingAgreementTab
    : COMMERCIAL_AGREEMENT_TAB_PARAMS.incomingAgreementTab

  const canAnswerRqHandleFeeSharingQuestion =
    needsReview && typeof agreement?.isRqHandleFeeSharing !== 'boolean'

  return (
    <>
      <div>
        {agreement?.isPendingAgreement && (
          <Alert variant="info" className="mb-4">
            <LuInfo className="mt-0.5" />

            <AlertDescription>
              <p>
                Please note that this agreement is still pending confirmation
                from both parties. Until confirmed by both parties, any referral
                activity will assume the{' '}
                <Button
                  variant="plain"
                  onClick={() => {
                    searchParams.set(
                      agreementTab,
                      COMMERCIAL_AGREEMENT_TAB_KEY.current,
                    )
                    setSearchParams(searchParams)
                  }}
                >
                  current agreement
                </Button>
                .
              </p>
            </AlertDescription>
          </Alert>
        )}

        {shouldShowPendingAgreementMessage && (
          <Alert variant="warning" className="my-4">
            {isPendingAgreementAwaitingReviewByUs ? (
              <LuCircleAlert />
            ) : (
              <LuInfo />
            )}

            <AlertDescription>
              <p className="mb-1!">
                {isPendingAgreementAwaitingReviewByUs
                  ? 'Changes to this current agreement are pending your review.'
                  : "Changes to this current agreement are pending your partner's review."}
              </p>

              <Button
                className={cn(
                  isPendingAgreementAwaitingReviewByUs
                    ? ''
                    : 'hover:bg-primary bg-white',
                )}
                variant={
                  isPendingAgreementAwaitingReviewByUs
                    ? 'success'
                    : 'outline-primary'
                }
                size={isPendingAgreementAwaitingReviewByUs ? 'lg' : undefined}
                onClick={() => {
                  const tabParam = isOutgoingAgreement
                    ? COMMERCIAL_AGREEMENT_TAB_PARAMS.outgoingAgreementTab
                    : COMMERCIAL_AGREEMENT_TAB_PARAMS.incomingAgreementTab

                  searchParams.set(
                    tabParam,
                    COMMERCIAL_AGREEMENT_TAB_KEY.pending,
                  )
                  setSearchParams(searchParams)
                }}
              >
                {isPendingAgreementAwaitingReviewByUs
                  ? 'Review changes now'
                  : 'View changes'}

                <FontAwesomeIcon icon={faArrowRight} />
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <Formik initialValues={initialValues} onSubmit={handleSubmit}>
          {({ values, setFieldValue }) => {
            function onApprove() {
              setFieldValue(
                REVIEW_COMMERCIAL_AGREEMENT_FORM_FIELDS.clientConsentLetterApprovedAt,
                new Date().toISOString(),
              )

              reviewESignatureLetterModal.hideModal()
            }

            function onAmend(amendRequest: string) {
              notyf.success('Amendment request added')

              setFieldValue(
                REVIEW_COMMERCIAL_AGREEMENT_FORM_FIELDS.clientConsentLetterAmendRequest,
                amendRequest,
              )

              reviewESignatureLetterModal.hideModal()
            }

            const consentLetterVariant =
              agreement?.toCompany?.incomingReferralsConsentLetterVariant ??
              REFERRAL_CONSENT_LETTER_VARIANT.DEFAULT

            return (
              <Form>
                {commercialAgreementService.shouldShowServiceFees(
                  agreement?.commercialAgreementType,
                ) && (
                  <div className="my-6">
                    <ServiceFeesTable
                      referralConsentStage={REFERRAL_CONSENT_STAGE.INTRODUCTION}
                      consentLetterVariant={consentLetterVariant}
                      agreementType={agreement?.commercialAgreementType}
                      commercialAgreementId={agreement?.id}
                      serviceFees={agreement?.serviceFees ?? []}
                      addAgreementLink={ROUTES.addRelationshipAgreement({
                        params: { panelId: panel.id.toString() },
                        searchParams: {
                          referralDirection,
                          returnPath: currentUrl(),
                        },
                      })}
                      isPreviewLetter={false}
                    />
                  </div>
                )}

                {agreement?.commercialAgreementType ? (
                  <>
                    <AgreementFieldRow
                      field={{
                        label: 'Agreement type',
                        value: commercialAgreementService.getAgreementTypeLabel(
                          agreement?.commercialAgreementType,
                        ),
                      }}
                    />

                    {agreement &&
                      commercialAgreementService.hasCommercialAgreement(
                        agreement?.commercialAgreementType,
                      ) && (
                        <AgreementFieldRow
                          field={{
                            label: 'eSignature via RQ PDF',
                            value: (
                              <Button
                                onClick={() =>
                                  previewESignatureLetterModal.showPreview(
                                    agreement.id,
                                  )
                                }
                                variant="outline-primary"
                                size="sm"
                                className="w-full sm:w-auto"
                              >
                                View PDF
                              </Button>
                            ),
                          }}
                        />
                      )}

                    {agreement.signedAgreementTemplateFileUrl ? (
                      <AgreementFieldRow
                        field={{
                          label: 'Standard terms PDF',
                          value: (
                            <Button asChild variant="outline-primary" size="sm">
                              <a
                                href={agreement.signedAgreementTemplateFileUrl}
                                target="_blank"
                                rel="noreferrer"
                              >
                                View PDF
                              </a>
                            </Button>
                          ),
                        }}
                      />
                    ) : (
                      <AgreementFieldRow
                        field={{
                          label: 'Existing agreements',
                          value: (
                            <ExistingAgreements
                              agreement={agreement}
                              needsReview={needsReview}
                            />
                          ),
                        }}
                      />
                    )}
                  </>
                ) : (
                  <AddAgreementSection
                    panel={panel}
                    referralDirection={referralDirection}
                  />
                )}

                {agreement?.commercialAgreementType ===
                  COMMERCIAL_AGREEMENT_TYPE_OPTION.YFS &&
                  isOutgoingAgreement && (
                    <AgreementFieldRow
                      field={{
                        label: (
                          <div className="flex items-center gap-1">
                            Would you like RQ to handle compliant fee sharing?
                            <RqCompliantFeeSharingTooltip />
                          </div>
                        ),
                        value: canAnswerRqHandleFeeSharingQuestion ? (
                          <RadioOptionsInput
                            label={false}
                            optionLabelStyle={{ height: '32px' }}
                            name={
                              REVIEW_COMMERCIAL_AGREEMENT_FORM_FIELDS.isRqHandleFeeSharing
                            }
                            options={OPTIONS.yesNo}
                          />
                        ) : (
                          <>{bool2Human(!!agreement.isRqHandleFeeSharing)}</>
                        ),
                        // message: values.isRqHandleFeeSharing &&
                        //   permissions.canManagePaymentProfile && (
                        //     <Suspense fallback={<SkeletonRows count={1} />}>
                        //       <div className="small font-normal text-right mt-2">
                        //         <PaymentProfileStatus />
                        //       </div>
                        //     </Suspense>
                        //   ),
                      }}
                    />
                  )}

                {referralDirection ===
                  COMMERCIAL_AGREEMENT_REFERRAL_DIRECTION.INCOMING &&
                  agreement?.isRqHandleFeeSharing && (
                    <Alert variant="info" className="my-4">
                      <LuCircleAlert className="mt-0.5" />

                      <AlertDescription>
                        <p>
                          {otherCompany.presentationName} has chosen RQ to
                          manage payments. In future, all fee sharing will need
                          to be paid directly to RQ rather than directly to{' '}
                          {otherCompany.presentationName}.
                        </p>
                      </AlertDescription>
                    </Alert>
                  )}

                {shouldReviewESignatureLetter(agreement) && (
                  <section className="mt-6">
                    <ReviewESignatureLetterBox
                      className="mb-0 border"
                      fromCompanyId={otherCompany.id}
                      toCompanyId={currentCompany.id}
                      fromCompanyName={otherCompany.presentationName}
                      consentLetterVariant={
                        currentCompany.incomingReferralsConsentLetterVariant
                      }
                      serviceFees={agreement?.serviceFees || []}
                      onApprove={onApprove}
                      onAmend={onAmend}
                      amendRequest={values.clientConsentLetterAmendRequest}
                      hasApproved={!!values.clientConsentLetterApprovedAt}
                      hasAddedAmendmentRequest={
                        !!values.clientConsentLetterAmendRequest
                      }
                      agreementStatus={agreement.status}
                    />
                  </section>
                )}

                {agreement && (
                  <AgreementStatusMessage
                    panel={panel}
                    referralDirection={referralDirection}
                    agreement={agreement}
                  />
                )}

                <div
                  className="mt-4 flex flex-col items-center gap-2 sm:flex-row"
                >
                  {renderActionButton()}

                  {agreement?.isPendingAgreement && (
                    <ActionButton
                      onClick={() => {
                        const hasConfirmed = window.confirm('Are you sure?')

                        if (hasConfirmed) {
                          deletePendingAgreementMutation.mutate(agreement)
                        }
                      }}
                      isProcessing={deletePendingAgreementMutation.isPending}
                      variant="outline-destructive"
                      size="lg"
                      className="inline-flex w-full items-center justify-center
                        gap-2 sm:w-auto"
                    >
                      <FontAwesomeIcon icon={faTimes} className="text-lg" />
                      {agreement.needsReview
                        ? 'Reject changes'
                        : 'Cancel changes'}
                    </ActionButton>
                  )}

                  {agreement?.commercialAgreementTemplate && (
                    <AmendTemplatedAgreementButton
                      commercialAgreementTemplateId={
                        agreement.commercialAgreementTemplate.id
                      }
                    />
                  )}

                  {shouldShowAmendButton(agreement) && (
                    <Button
                      asChild
                      variant="outline-primary"
                      size="lg"
                      className="inline-flex w-full items-center justify-center
                        gap-2 sm:w-auto"
                    >
                      <Link
                        to={ROUTES.commercialAgreementEdit({
                          params: {
                            commercialAgreementId: agreement.id.toString(),
                          },
                          searchParams: { returnPath: currentUrl() },
                        })}
                      >
                        <FontAwesomeIcon icon={faPencil} />
                        Amend agreement
                      </Link>
                    </Button>
                  )}
                </div>
              </Form>
            )
          }}
        </Formik>
      </div>

      {agreement &&
        searchParams.get(
          PREVIEW_ESIGNATURE_LETTER_MODAL_PARAMS.isPreviewingESignatureLetterForAgreementId,
        ) === agreement.id.toString() && (
          <PreviewESignatureLetterModal
            commissionJustification={
              agreement.commissionJustification || undefined
            }
            fromCompanyId={agreement.fromCompany.id}
            toCompanyId={agreement.toCompany.id}
            title="eSignature via RQ PDF"
            consentLetterVariant={
              agreement.toCompany.incomingReferralsConsentLetterVariant
            }
            serviceFees={agreement?.serviceFees || []}
            agreementStatus={agreement.status}
            renderHeader={() => (
              <p className="mb-0">
                This is what an eSignature letter via RQ will look like:
              </p>
            )}
          />
        )}
    </>
  )
}

export default DirectionAgreementDetails
